/**
 * legalContent.ts  -  Source text for all public legal pages.
 *
 * NOTE: This is a good-faith template written for a children's education
 * platform under UK GDPR / ePrivacy. It is NOT a substitute for review by a
 * qualified data-protection lawyer before commercial launch.
 */

export interface LegalSection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
}

export interface LegalDoc {
  slug: string;
  title: string;
  updated: string;
  intro?: string;
  sections: LegalSection[];
}

const UPDATED = "12 June 2026";
const CONTACT = "privacy@litlings.com";

export const LEGAL_DOCS: Record<string, LegalDoc> = {
  privacy: {
    slug: "privacy",
    title: "Privacy Policy",
    updated: UPDATED,
    intro:
      "Litlings helps teachers teach phonics to young children. We take the privacy of children, teachers and parents seriously. This policy explains what we collect, why, who we share it with, and your rights.",
    sections: [
      {
        heading: "1. Who is responsible for your data",
        paragraphs: [
          "When a school or teacher uses Litlings with their pupils, the school is the data controller and Litlings acts as a data processor on the school’s behalf, under a Data Processing Agreement (see our DPA).",
          "For teacher and parent accounts you create directly with us (registration, billing, support), Litlings is the controller.",
        ],
      },
      {
        heading: "2. What we collect",
        bullets: [
          "Account data: name, email, password (hashed), role, school.",
          "Student profile data: display name, year/phonics level, class membership, join codes. We ask schools not to use full legal names where a first name or nickname will do.",
          "Learning data: game results, scores, XP, streaks, badges, lesson/homework progress, spelling results.",
          "Submissions: photos of written work and short voice recordings that pupils upload for marking.",
          "Live session data: presence, hand-raises, chat messages, and (if enabled) video/audio during a live lesson.",
          "Technical data: IP address, device/browser type, and first-party usage analytics (which features are used).",
          "Billing data: handled by Stripe; we never see or store full card numbers.",
        ],
      },
      {
        heading: "3. Children’s data",
        paragraphs: [
          "Litlings is designed for use by children under the direction of a teacher or parent. We rely on the school or parent to provide the appropriate consent or lawful basis for a child to use the service.",
          "We practise data minimisation: we collect only what is needed to teach phonics and report progress. Raw submission media (photos and voice clips) is automatically deleted after a retention period; scores and progress metadata are kept so learning history is preserved.",
        ],
      },
      {
        heading: "4. Lawful bases",
        bullets: [
          "Performance of a contract  -  to provide the service to schools and account holders.",
          "Legitimate interests  -  to secure, maintain and improve the service (balanced against your rights).",
          "Consent  -  for non-essential cookies/analytics and any optional communications.",
          "Legal obligation  -  where we must retain or disclose data by law (e.g. safeguarding).",
        ],
      },
      {
        heading: "5. Third parties and AI sub-processors",
        paragraphs: [
          "Some features send data to trusted sub-processors strictly to deliver the service:",
        ],
        bullets: [
          "OpenAI  -  AI pre-scoring of photo submissions (GPT-4o-mini Vision). A submission image is sent for automated scoring; a teacher always reviews the result.",
          "Groq  -  transcription of student voice recordings. An audio clip is sent for speech-to-text; audio is not retained by Groq.",
          "Stripe  -  payment processing for paid plans.",
          "Migadu (SMTP)  -  transactional emails (account verification, receipts, trial notices).",
          "LiveKit / Jitsi  -  real-time video for live lessons (only when a teacher starts a video call).",
          "Hosting  -  our servers run on a dedicated VPS (Contabo) in the EU.",
        ],
      },
      {
        heading: "6. AI scoring  -  how automated decisions are used",
        paragraphs: [
          "AI is used only to suggest a score to the teacher. It is not a final, solely-automated decision: a human teacher confirms, overrides, or rescores every submission before it affects a child’s record. You can ask your teacher/school to disable AI pre-scoring.",
        ],
      },
      {
        heading: "7. How long we keep data",
        bullets: [
          "Raw submission media (photos/voice): automatically deleted after the retention period set for your plan.",
          "Scores, progress and gamification data: kept while the account is active so learning history is preserved.",
          "Account data: deleted on request or a reasonable period after an account is closed.",
          "Backups: rotated on a rolling schedule and then overwritten.",
        ],
      },
      {
        heading: "8. Your rights",
        paragraphs: [
          "Under UK/EU GDPR you have the right to access, correct, delete, restrict, object to, and port your personal data. Children’s rights are usually exercised by the school or parent.",
          `To make a request, contact ${CONTACT}. For school-managed pupil data, contact your school, who will instruct us as processor. You may also complain to the UK ICO (or your local supervisory authority).`,
        ],
      },
      {
        heading: "9. Security",
        paragraphs: [
          "We use HTTPS everywhere, hashed passwords, signed tokens, role-based access, and least-privilege storage access. See our Security page for details. No system is perfectly secure; we work to reduce risk and respond quickly to incidents.",
        ],
      },
      {
        heading: "10. International transfers",
        paragraphs: [
          "Core data is hosted in the EU. Some sub-processors (e.g. OpenAI, Stripe) may process data outside the UK/EU under appropriate safeguards such as Standard Contractual Clauses.",
        ],
      },
      {
        heading: "11. Changes & contact",
        paragraphs: [
          `We will post any material changes here and update the date above. Questions: ${CONTACT}.`,
        ],
      },
    ],
  },

  terms: {
    slug: "terms",
    title: "Terms of Service",
    updated: UPDATED,
    intro:
      "These terms govern your use of Litlings. By creating an account or using the service you agree to them.",
    sections: [
      {
        heading: "1. The service",
        paragraphs: [
          "Litlings is a web platform for teaching and practising phonics, with games, live lessons, marking, gamification and reporting. We may add, change or remove features over time.",
        ],
      },
      {
        heading: "2. Accounts & eligibility",
        bullets: [
          "Teacher, school-admin and parent accounts are for adults (18+).",
          "Child access is provided by a supervising teacher or parent, who is responsible for obtaining any needed consent.",
          "You are responsible for keeping your credentials secure and for activity under your account.",
        ],
      },
      {
        heading: "3. Plans, trials & billing",
        bullets: [
          "Spark is free. Paid plans (Grow, School, Trust) are billed via Stripe; bespoke arrangements are by agreement.",
          "Paid plans may include a free trial. If you do not upgrade, paid features become unavailable but your data is retained for a reasonable period.",
          "Fees are per the pricing shown at purchase. Taxes may apply. You can cancel anytime via the billing portal; access continues until the end of the paid period.",
          "Usage-based features (e.g. AI scoring) may be subject to fair-use quotas described on the pricing page.",
        ],
      },
      {
        heading: "4. Acceptable use",
        paragraphs: [
          "You must use Litlings lawfully and in line with our Acceptable Use Policy. In particular, do not upload content that is unlawful, harmful, or that infringes others’ rights, and do not attempt to disrupt or reverse-engineer the service.",
        ],
      },
      {
        heading: "5. Your content",
        paragraphs: [
          "You (or your school) retain ownership of the content you upload. You grant us a limited licence to host and process it solely to provide the service (including AI marking, where enabled).",
        ],
      },
      {
        heading: "6. Availability",
        paragraphs: [
          "We aim for high availability and work to keep the service running, but we provide the service on a best-effort basis and do not currently guarantee a specific uptime figure unless stated in a separate Enterprise agreement.",
        ],
      },
      {
        heading: "7. Liability",
        paragraphs: [
          "To the extent permitted by law, Litlings is provided “as is”. We are not liable for indirect or consequential loss. Nothing limits liability that cannot be limited by law.",
        ],
      },
      {
        heading: "8. Termination",
        paragraphs: [
          "You may stop using the service anytime. We may suspend or terminate accounts that breach these terms or the Acceptable Use Policy. On termination we handle data per our Privacy Policy.",
        ],
      },
      {
        heading: "9. Governing law & contact",
        paragraphs: [
          `These terms are governed by the laws of England and Wales. Questions: ${CONTACT}.`,
        ],
      },
    ],
  },

  cookies: {
    slug: "cookies",
    title: "Cookie Policy",
    updated: UPDATED,
    intro:
      "This policy explains the cookies and similar technologies Litlings uses.",
    sections: [
      {
        heading: "1. What we use",
        bullets: [
          "Essential  -  a secure, HttpOnly cookie for keeping you signed in (session/refresh token). The service cannot work without this; it does not require consent.",
          "Local storage  -  we store your access token and small preferences (e.g. phonics level) in your browser to keep the app responsive.",
          "Analytics (first-party)  -  we record which features are used to improve the product. This is our own first-party analytics; we do not use third-party advertising or tracking SDKs. It runs only if you accept analytics in the cookie banner.",
        ],
      },
      {
        heading: "2. What we do NOT use",
        bullets: [
          "No advertising cookies.",
          "No third-party social or ad-network trackers.",
          "No cross-site tracking or data brokering.",
        ],
      },
      {
        heading: "3. Managing cookies",
        paragraphs: [
          "You can accept or decline analytics in the cookie banner, and change your choice anytime by clearing site data. Blocking essential cookies will prevent sign-in.",
        ],
      },
      {
        heading: "4. Contact",
        paragraphs: [`Questions: ${CONTACT}.`],
      },
    ],
  },

  safeguarding: {
    slug: "safeguarding",
    title: "Safeguarding & Child Protection",
    updated: UPDATED,
    intro:
      "Children’s safety comes first. This page explains the protections built into Litlings.",
    sections: [
      {
        heading: "1. Adult-supervised by design",
        paragraphs: [
          "Children use Litlings under a teacher or parent. Children do not create their own accounts and cannot contact people outside their class.",
        ],
      },
      {
        heading: "2. Controlled communication",
        bullets: [
          "Chat and messaging are scoped to a class/session and visible to the supervising teacher.",
          "There is no open or public chat, no friend requests, and no direct child-to-stranger messaging.",
          "Display names are resolved server-side to reduce impersonation.",
        ],
      },
      {
        heading: "3. Wellbeing flags",
        paragraphs: [
          "The platform can raise wellbeing flags for staff to review. These are decision-support signals for trained adults, not automated judgements about a child.",
        ],
      },
      {
        heading: "4. Data minimisation",
        paragraphs: [
          "We encourage first names or nicknames over full legal names, and we auto-delete raw submission media after a retention period.",
        ],
      },
      {
        heading: "5. Reporting a concern",
        paragraphs: [
          "If you have a safeguarding concern about content or behaviour on the platform, contact us immediately at safeguarding@litlings.com. Genuine emergencies should always go to your local emergency services and your school’s designated safeguarding lead first.",
        ],
      },
    ],
  },

  "acceptable-use": {
    slug: "acceptable-use",
    title: "Acceptable Use Policy",
    updated: UPDATED,
    intro:
      "To keep Litlings safe for children and teachers, all users agree to these rules.",
    sections: [
      {
        heading: "You must not",
        bullets: [
          "Upload unlawful, abusive, hateful, sexual, violent or otherwise harmful content.",
          "Upload anyone’s personal data beyond what is needed to teach (e.g. no unnecessary identifying photos).",
          "Impersonate another person or misuse another account.",
          "Attempt to access data you are not authorised to see.",
          "Probe, scan, overload, disrupt or reverse-engineer the service.",
          "Use the service to contact children outside a legitimate teaching relationship.",
          "Resell or sublicense the service without permission.",
        ],
      },
      {
        heading: "Enforcement",
        paragraphs: [
          "We may remove content and suspend or terminate accounts that breach this policy, and report unlawful activity to the relevant authorities.",
        ],
      },
      {
        heading: "Contact",
        paragraphs: [`Report misuse: ${CONTACT}.`],
      },
    ],
  },

  security: {
    slug: "security",
    title: "Security",
    updated: UPDATED,
    intro: "How we protect Litlings data.",
    sections: [
      {
        heading: "In transit & at rest",
        bullets: [
          "HTTPS/TLS on all traffic, with automatic certificate renewal.",
          "Passwords stored using strong one-way hashing; we never store them in plain text.",
          "Signed, short-lived access tokens with rotating refresh tokens stored in HttpOnly cookies.",
        ],
      },
      {
        heading: "Access control",
        bullets: [
          "Role-based access (platform admin, school admin, teacher, parent, student).",
          "Data is scoped to a user’s school and classes.",
          "Object storage uses least-privilege, time-limited access for media.",
        ],
      },
      {
        heading: "Operational",
        bullets: [
          "Dependency and error monitoring.",
          "Input validation on the server for all requests.",
          "Regular database backups on a rolling schedule.",
        ],
      },
      {
        heading: "Responsible disclosure",
        paragraphs: [
          "Found a vulnerability? Please report it privately to security@litlings.com before disclosing publicly. We will acknowledge and work with you on a fix.",
        ],
      },
    ],
  },

  dpa: {
    slug: "dpa",
    title: "Data Processing Agreement (Schools)",
    updated: UPDATED,
    intro:
      "Summary DPA for schools using Litlings with pupils. When a school uses Litlings, the school is the controller and Litlings is the processor. A full signable DPA is available on request for Grow, School and Trust plans.",
    sections: [
      {
        heading: "1. Roles",
        paragraphs: [
          "The school determines the purposes and means of processing pupil data (controller). Litlings processes that data only on the school’s documented instructions (processor).",
        ],
      },
      {
        heading: "2. Subject matter & duration",
        paragraphs: [
          "Processing is for the purpose of delivering phonics teaching, marking and reporting, for the duration of the subscription and any agreed retention period thereafter.",
        ],
      },
      {
        heading: "3. Categories of data & subjects",
        bullets: [
          "Data subjects: pupils, teachers, and (where used) parents.",
          "Data: identifiers (display name, class), learning results, submission media, and limited technical data.",
        ],
      },
      {
        heading: "4. Sub-processors",
        paragraphs: [
          "We use the sub-processors listed in our Privacy Policy (e.g. OpenAI, Google Vision, Stripe, Resend, LiveKit/Jitsi, hosting). We will inform schools of material changes and bind sub-processors to equivalent obligations.",
        ],
      },
      {
        heading: "5. Security, breaches & assistance",
        bullets: [
          "We apply the technical and organisational measures described on our Security page.",
          "We will notify the school without undue delay on becoming aware of a personal-data breach.",
          "We will assist the school with data-subject requests, DPIAs and audits as required by Article 28 GDPR.",
        ],
      },
      {
        heading: "6. Return & deletion",
        paragraphs: [
          "On termination, we will delete or return pupil personal data per the school’s instruction and our retention schedule.",
        ],
      },
      {
        heading: "7. Request a signed DPA",
        paragraphs: [
          `Email ${CONTACT} to receive a countersigned DPA for your school.`,
        ],
      },
    ],
  },
};

export const LEGAL_SLUGS = Object.keys(LEGAL_DOCS);
